import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from "react";
import { API, Auth, Hub } from "aws-amplify";
import Modal from "react-bootstrap/Modal";
import Dropdown from "react-bootstrap/Dropdown";
import Spinner from "components/Spinner";
import OpenQueryReminderModal from "components/OpenQueryReminderModal";
import {
  PhGridPagination,
  PhGridFilter,
  usePagination,
  PhGridSort
} from "components/PhGrid";
import { remindersEnabled } from "../../flags";
import { cookieAttributes } from "util/resumeCookieSettings";
import { useHistory, useLocation } from "react-router-dom";
import { useOpenQueryBridge } from "features/queryReports/utils/commonFunctions";
import {
  marketShareIDs,
  clinicalMarketShareIDs,
  populationTrackerIDs,
  clinicalVolumesIDs,
  trendedPOSIDs,
  placeOfServiceByCountyIDs,
  placeOfServiceIDs,
  billingReportIDs,
  countyLevelServicesIDs,
  shareOfCareIDs,
  campaignEffectivenessIDs
} from "features/queryReports/utils/reportIds";
import MarketShare from "../QueryDetails/reports/MarketShare";
import ClinicalMarketShare from "../QueryDetails/reports/ClinicalMarketShare";
import PopulationTracker from "components/QueryDetails/reports/PopulationTracker";
import ClinicalVolumes from "components/QueryDetails/reports/ClinicalVolumes";
import PlaceOfServiceByCounty from "components/QueryDetails/reports/PlaceOfServiceByCounty";
import TrendedPOS from "components/QueryDetails/reports/TrendedPOS";
import PlaceOfService from "components/QueryDetails/reports/PlaceOfService";
import BillingReport from "components/QueryDetails/reports/BillingReport";
import ShareOfCare from "components/QueryDetails/reports/ShareOfCare";
import CampaignEffectiveness from "components/QueryDetails/reports/CampaignEffectiveness";
import CountyLevelServicesReport from "components/QueryDetails/reports/CountyLevelServices";
import { Breadcrumbs } from "features/breadcrumb/breadcrumbs";
import classes from "./OpenQueryQueries.module.scss";
import { buildQueryBreadcrumbs } from "features/queryReports/utils/commonFunctions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartColumn,
  faDownload,
  faEllipsisVertical,
  faFilterCircleXmark
} from "@fortawesome/free-solid-svg-icons";
/** Local dev uses localhost; otherwise use env-provided domain.
 *  If the env var is missing in non-local, we fail fast with example.invalid.
 */

function isExpired(createdDate) {
  if (!createdDate) return "-";
  const purgeTime = createdDate.getTime() + 90 * 24 * 3600 * 1000; // +90 days
  const now = Date.now();
  const days = (purgeTime - now) / (1000 * 3600 * 24);
  return days < 0;
}

const addDays = (date, days) => {
  if (!date) return "";
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString();
};

const getDisplayStatus = (status, dateCreated) => {
  // if  90 days from the date of submission - expired, processed - successfully been executed, rest - In-process
  if (isExpired(dateCreated)) {
    return { label: "Expired", key: "expired" };
  }
  if (status === "PROCESSED") {
    return { label: "Processed", key: "processed" };
  }
  return { label: "Query in Process", key: "pending" };
};


function QueryRow(props) {
  const handleShowResubmitModal = () => {
    props.setModalCategoryId(props["category-id"]);
    props.setModalResubmitData({
      definition: props.definition,
      categoryId: props["category-id"],
      reportId: props.reportId,
      description: props.description
    });
    props.setShowResubmitModal(true);
  };

  const handleShowReminderModal = () => {
    props.setReminderJob(props.job);
    props.setShowReminderModal(true);
  };

  const history = useHistory();

  const getTextClass = (type, classes) => {
    return `${classes["status-text"]} ${classes[type?.toLowerCase()] || ""}`;
  };

  const isButtonDisabled =
    isExpired(props.dateCreated) || props.status !== "PROCESSED";


  const statusMeta = getDisplayStatus(props.status, props.dateCreated);

  return (
    <tr key={props.id}>
      <td scope="row">{props.description || ""}</td>
      <td>{props.report.split(".").join(", ")}</td>
      <td>{addDays(props?.dateCreated, 90)}</td>
      <td>{props.dateCreated.toLocaleDateString()}</td>
      <td className="text-center">
        <span className={getTextClass(statusMeta.key, classes)}>
          {statusMeta.label}
        </span>
      </td>
      <td className="text-center">
        <span className={`${classes["download-btn"]} ${isButtonDisabled && classes["disabled"]}`}>
          <FontAwesomeIcon icon={faDownload} />
          <a
            className={`${classes["download-link"]} ${isButtonDisabled && classes["download-disabled"]}`}
            href={isButtonDisabled ? undefined : props.signed_url}
            tabIndex={isButtonDisabled ? -1 : undefined}
            aria-disabled={isButtonDisabled ? "true" : undefined}
            download
          >
            Download
          </a>
        </span>
      </td>
      <td className="text-center">
        {props.canBeVisualized && (
          <button
            type="button"
            disabled={isButtonDisabled}
            className={`btn ${isButtonDisabled
              ? `btn-secondary ${classes.disabled}`
              : `btn-success ${classes.enabledVizbtn}`
              } ${classes.visualizeBtn}`}
            onClick={() => {
              if (isButtonDisabled) return;
              history.push(`/queryvisualization/${props.id}`, {
                queryName: props.description,
                dashboards: false
              });
            }}
          >
            <FontAwesomeIcon icon={faChartColumn} /> Visualize
          </button>
        )}
      </td>
      <td className="text-center">
        {props.status === "PROCESSED" && (
          <Dropdown>
            <Dropdown.Toggle
              variant="link"
              className="p-0 border-0 shadow-none"
              id={`actions-${props.id}`}
            >
              <FontAwesomeIcon
                icon={faEllipsisVertical}
                className={classes["threeDotIcon"]}
              />
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item onClick={handleShowResubmitModal}>
                Resubmit Query
              </Dropdown.Item>
              {remindersEnabled && (
                <Dropdown.Item onClick={handleShowReminderModal}>
                  Schedule Query Reminder
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        )}
      </td>
    </tr>
  );
}

async function getToken() {
  await Auth.currentAuthenticatedUser(); // ensure session exists
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (err) {
    console.error("error getting token", err);
  }
}

function OpenQueryQueriesContainer() {
  useEffect(() => {
    async function setResumeCookie() {
      const cookieValue = encodeURIComponent(`QUERY - /openquery/commercial`);
      const resumeCookie = Object.assign(
        cookieAttributes(),
        { name: "platform_resume" },
        { value: cookieValue }
      );
      await cookieStore.set(resumeCookie);
    }
    setResumeCookie();
  }, []);

  const {
    paginationOffset,
    setPaginationOffset,
    paginationSize,
    setPaginationSize,
    filterString,
    setFilterString,
    sortColumn,
    setSortColumn,
    sortKey,
    setSortKey,
    sortDirection,
    setSortDirection
  } = usePagination();
  const history = useHistory();
  const location = useLocation();
  const [queries, setQueries] = useState(null);
  const [showResubmitModal, setShowResubmitModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [modalCategoryId, setModalCategoryId] = useState("");
  const [modalResubmitData, setModalResubmitData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [reminderJob, setReminderJob] = useState(null);
  const [queryType, setQueryType] = useState("ALL");

  const handleClear = () => {
    setPaginationOffset(0);
    setQueryType("All");
    setFilterString("");
    setSortColumn("");
  };

  const bridge = useOpenQueryBridge({
    history,
    getToken,
    setShowResubmitModal
  });
  const handleCloseResubmitModal = () => setShowResubmitModal(false);
  const handleCloseReminderModal = () => setShowReminderModal(false);
  const showOpenMyQueries = location.pathname !== "/openquery/queries";
  // Auth readiness + in-flight guard
  const [ready, setReady] = useState(false);
  const inFlight = useRef(false);

  const breadCrumbsItems = buildQueryBreadcrumbs({
    customItems: [
      { label: "Home", to: "/" },
      { label: "Query", to: "/openquery" },
      { label: "My Queries", to: "/openquery/queries" }
    ]
  });

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setQueries(null);
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // Fetch queries only when authenticated
  useEffect(() => {
    if (!ready) return;
    if (inFlight.current) return;
    inFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser(); // double-check
        const jobs = await API.get("OpenQueryAPI", `/openquery/jobs`);
        if (cancelled) return;

        const requests = jobs?.requests || [];
        const files = jobs?.files || [];

        const filesMap = new Map(files.map(file => [file.id, file]));

        const mergedJobs = requests.map(request => ({
          ...request,
          ...(filesMap.get(request.id) || {})
        }));

        const shaped = (mergedJobs || []).map(job => ({
          ...job,
          dateCreated: new Date(job.dateCreated)
        }))
          .sort((a, b) => { // sort reverse chronological order.
            if (!a?.dateCreated) return 1;
            if (!b?.dateCreated) return -1;
            return b.dateCreated - a.dateCreated;
          });

        setQueries(shaped);
      } catch (err) {
        console.error("error fetching openquery jobs", err);
      } finally {
        inFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready]);

  const matchesQueryType = (report, queryType) => {
    if (queryType === "ALL") return true;

    const suffix = report.split(".").pop();

    if (queryType === "MEDICARE") {
      return suffix === "Medicare";
    }

    if (queryType === "COMBO") {
      return suffix === "Combo";
    }

    if (queryType === "COMMERCIAL") {
      return suffix !== "Medicare" && suffix !== "Combo";
    }

    return true;
  };

  const filteredListItems = useMemo(() => {
    if (!queries) return [];

    return queries
      .filter(query => {
        const statusMeta = getDisplayStatus(query.status, query.dateCreated);
        const matchesSearch = [
          query.description || "",
          query.report.split(".").join(", "),
          statusMeta.label
        ]
          .join("")
          .toLowerCase()
          .includes(filterString.toLowerCase());

        const matchesType = matchesQueryType(query.report, queryType);

        return matchesSearch && matchesType;
      })
      .sort((a, b) => {
        if (!sortColumn) return 0;

        if (sortDirection === "ascending") {
          return sortKey(a) > sortKey(b) ? 1 : sortKey(a) < sortKey(b) ? -1 : 0;
        } else {
          return sortKey(a) > sortKey(b) ? -1 : sortKey(a) < sortKey(b) ? 1 : 0;
        }
      });
  }, [queries, filterString, queryType, sortColumn, sortKey, sortDirection]);

  const displayList = useMemo(() => {
    return filteredListItems.slice(
      paginationOffset,
      paginationOffset + paginationSize
    );
  }, [filteredListItems, paginationOffset, paginationSize]);

  const handleQueryType = e => {
    setQueryType(e.target.value);
    setPaginationOffset(0);
  };


  const renderReport = useCallback(
    categoryId => {
      try {
        const propsData = {
          bridge: bridge,
          initialParams: modalResubmitData,
          showOpenMyQueries: showOpenMyQueries
        };
        switch (categoryId) {
          case marketShareIDs.categoryId:
            return <MarketShare {...propsData} />;
          case clinicalMarketShareIDs.categoryId:
            return <ClinicalMarketShare {...propsData} />;
          case populationTrackerIDs.categoryId:
            return <PopulationTracker {...propsData} />;
          case clinicalVolumesIDs.categoryId:
            return <ClinicalVolumes {...propsData} />;
          case trendedPOSIDs.categoryId:
            return <TrendedPOS {...propsData} />;
          case placeOfServiceByCountyIDs.categoryId:
            return <PlaceOfServiceByCounty {...propsData} />;
          case placeOfServiceIDs.categoryId:
            return <PlaceOfService {...propsData} />;
          case billingReportIDs.categoryId:
            return <BillingReport {...propsData} />;
          case countyLevelServicesIDs.categoryId:
            return <CountyLevelServicesReport {...propsData} />;
          case shareOfCareIDs.categoryId:
            return <ShareOfCare {...propsData} />;
          case campaignEffectivenessIDs.categoryId:
            return <CampaignEffectiveness {...propsData} />;
          default:
            return <div>Error finding report type. Please try again.</div>;
        }
      } catch (error) {
        return <div>Error loading report. Please try again.</div>;
      }
    },
    [modalResubmitData]
  );

  return (
    <>
      <div className={`query-wrapper ${classes["create-query-wrapper"]}`}>
        <Breadcrumbs items={breadCrumbsItems} />
        <div className={classes["head-wrapper"]}>
          <h3 className={classes["heading"]}>My Queries</h3>
          <span className={classes["subtitle"]}>
            View, manage, and run your saved data queries
          </span>
        </div>
        <div>
          <div
            className={`${classes["content-wraper"]} ${classes["filter-query-list-wrapper"]}`}
          >
            {queries && (
              <div className="">
                <PhGridFilter
                  placeholder="Search queries by name, type, or more…"
                  filterString={filterString}
                  setFilterString={setFilterString}
                  setPaginationOffset={setPaginationOffset}
                />
                <div className={classes["section-wrap"]}>
                  <div className={classes["query-type-drpdwn"]}>
                    <label className={classes["label"]}>Query Types</label>
                    <select value={queryType} onChange={handleQueryType}>
                      <option value="ALL">All Types</option>
                      <option value="COMMERCIAL">Commercial</option>
                      <option value="MEDICARE">Medicare</option>
                      <option value="COMBO">Commercial & Medicare</option>
                    </select>
                  </div>

                  <button
                    onClick={handleClear}
                    className={classes["clear-btn"]}
                  >
                    <FontAwesomeIcon icon={faFilterCircleXmark} /> Clear Filters
                  </button>
                </div>
              </div>
            )}
          </div>
          <div
            className={`${classes["content-wraper"]} ${classes["query-list-wrapper"]}`}
          >
            <h4 className={classes["query-list-title"]}>Queries List</h4>
            <table
              className="table table-striped table-responsive"
              style={{ padding: 0 }}
            >
              <thead>
                <tr>
                  <th scope="col">
                    <PhGridSort
                      columnLabel="Query Name"
                      columnName={d => d?.description}
                      config={{
                        sortKey,
                        setSortKey,
                        sortDirection,
                        setSortDirection,
                        sortColumn,
                        setSortColumn
                      }}
                    />
                  </th>
                  <th scope="col">
                    <PhGridSort
                      columnLabel="Report Name"
                      columnName={d => d?.report.split(".").join(", ")}
                      config={{
                        sortKey,
                        setSortKey,
                        sortDirection,
                        setSortDirection,
                        sortColumn,
                        setSortColumn
                      }}
                    />
                  </th>
                  <th scope="col" className="text-center">
                    <PhGridSort
                      columnLabel="Expires"
                      columnName={d => addDays(d?.dateCreated, 90)}
                      config={{
                        sortKey,
                        setSortKey,
                        sortDirection,
                        setSortDirection,
                        sortColumn,
                        setSortColumn
                      }}
                    />
                  </th>
                  <th scope="col" className="text-center">
                    <PhGridSort
                      columnLabel="Created"
                      columnName={d => d?.dateCreated}
                      config={{
                        sortKey,
                        setSortKey,
                        sortDirection,
                        setSortDirection,
                        sortColumn,
                        setSortColumn
                      }}
                    />
                  </th>
                  <th scope="col" className="text-center">
                    <PhGridSort
                      columnLabel="Status"
                      columnName={d => d?.status}
                      config={{
                        sortKey,
                        setSortKey,
                        sortDirection,
                        setSortDirection,
                        sortColumn,
                        setSortColumn
                      }}
                    />
                  </th>
                  <th scope="col" className="text-center">
                    Download
                  </th>
                  <th scope="col" className="text-center">
                    Visualize
                  </th>
                  <th scope="col" className="text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {!queries && (
                  <tr>
                    <td colSpan="8" className="text-center">
                      <Spinner />
                    </td>
                  </tr>
                )}

                {(!displayList || displayList.length === 0) && (
                  <tr>
                    <td colSpan={8} className={`${classes["no-results"]} text-center`}>
                      No results found
                    </td>
                  </tr>
                )}

                {displayList.map(item => (
                  <QueryRow
                    {...item}
                    key={item.id}
                    setShowResubmitModal={setShowResubmitModal}
                    setModalCategoryId={setModalCategoryId}
                    setModalResubmitData={setModalResubmitData}
                    setShowReminderModal={setShowReminderModal}
                    job={item}
                    setReminderJob={setReminderJob}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className={classes["pagination-wrapper"]}>
        {queries && (
          <div>
            <PhGridPagination
              fullList={queries}
              filteredListItems={filteredListItems}
              paginationOffset={paginationOffset}
              setPaginationOffset={setPaginationOffset}
              setPaginationSize={setPaginationSize}
              paginationSize={paginationSize}
              filterString={filterString}
            />
          </div>
        )}
      </div>
      <Modal show={errorMsg !== ""} onHide={() => setErrorMsg("")}>
        <Modal.Header closeButton>
          <Modal.Title>Error</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{errorMsg}</p>
        </Modal.Body>
      </Modal>

      <Modal
        size="lg"
        show={showResubmitModal}
        onHide={handleCloseResubmitModal}
        className="resubmit-query"
      >
        <Modal.Header closeButton>
          <Modal.Title>Resubmit Query</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                {showResubmitModal &&
                  modalCategoryId &&
                  renderReport(modalCategoryId)}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {remindersEnabled && (
        <OpenQueryReminderModal
          title="Schedule Reminder"
          job={reminderJob}
          showReminderModal={showReminderModal}
          handleCloseReminderModal={handleCloseReminderModal}
        />
      )}
    </>
  );
}

export default OpenQueryQueriesContainer;
